import {
  fetchAllAnthropicCosts,
  determineAnthropicBucketWidth,
  calculateAnthropicCostBreakdown,
  getAnthropicTokenBreakdown
} from '../anthropic-api';
import { getAnthropicApiKey, readAdminCredentialRevision } from '../storage';
import type {
  ApiResponse,
  UsageDetailsData,
  ModelUsageDetail,
  ApiKeyUsageDetail,
  DailyUsageData,
  ProviderUsageMetrics,
  UsageProvider
} from '../openai-api';
import { dedup, buildSegmentDedupKey } from '../request-dedup';
import type {
  AnthropicUsageAdapterOptions,
  ApiKeyAccumulator,
  DailyAnthropicUsageAggregate
} from './types';
import {
  CONSOLE_KEY_ID,
  MS_PER_DAY,
  TOKENS_PER_REQUEST_CLAUDE_CODE,
  TOKENS_PER_REQUEST_API,
  CACHE_READ_REQUEST_WEIGHT,
  formatUtcBucketKey
} from './constants';
import { fetchUsageWithinRange } from './segment-cache';
import {
  summarizeAnthropicCosts,
  scaleCostBreakdowns,
  distributeCostByTokenShare
} from './cost-reconciliation';
import {
  createAnthropicDailyAggregate,
  determineRank,
  ensureMetadataCache,
  ensureWorkspaceCache,
  ensureUserCache,
  getDefaultRange
} from './aggregation';
import { debugLog } from '../debug-log';

export const getAnthropicUsageDetails = async (
  options?: AnthropicUsageAdapterOptions,
  onProgress?: (message: string) => void,
  executionContext?: { apiKey?: string; credentialRevision?: string }
): Promise<ApiResponse<UsageDetailsData>> => {
  const apiKey = executionContext?.apiKey ?? getAnthropicApiKey();
  if (!apiKey) {
    return {
      success: false,
      error: 'Anthropic Admin API key is not configured'
    };
  }
  const credentialRevision = executionContext?.credentialRevision
    ?? readAdminCredentialRevision();

  const actualRange = options ?? getDefaultRange();
  const { startDate, endDate } = actualRange;

  try {
    const anthropicBucketWidth = determineAnthropicBucketWidth(startDate, endDate);
    const dedupKey = buildSegmentDedupKey(
      'anthropic',
      anthropicBucketWidth,
      Math.floor(startDate.getTime() / 1000),
      Math.floor(endDate.getTime() / 1000),
      credentialRevision
    );
    const costDedupKey = [
      'anthropic-cost',
      credentialRevision,
      Math.floor(startDate.getTime() / 1000),
      Math.floor(endDate.getTime() / 1000)
    ].join(':');

    const costSummaryPromise = anthropicBucketWidth === '1d'
      ? dedup(costDedupKey, async () => {
          onProgress?.('Fetching Anthropic cost report...');
          const rows = await fetchAllAnthropicCosts(startDate, endDate, ['workspace_id', 'description']);
          return summarizeAnthropicCosts(rows);
        }).catch((error: unknown) => {
          const message = error instanceof Error ? error.message : String(error);
          console.warn('[Anthropic Usage] Cost report unavailable, keeping estimated cost values', {
            message
          });
          return null;
        })
      : Promise.resolve(null);

    const [usageResult, metadataCache, workspaceCache, userCache, costSummaryResult] = await Promise.all([
      dedup(dedupKey, () => fetchUsageWithinRange(
        startDate,
        endDate,
        onProgress,
        credentialRevision
      )),
      ensureMetadataCache(credentialRevision),
      ensureWorkspaceCache(credentialRevision),
      ensureUserCache(credentialRevision),
      costSummaryPromise
    ]);
    const usageData = usageResult.data;
    const partialErrors = [...usageResult.partialErrors];
    if (metadataCache.refreshError) {
      partialErrors.push(`API key metadata unavailable: ${metadataCache.refreshError}`);
    }
    if (workspaceCache.refreshError) {
      partialErrors.push(`Workspace metadata unavailable: ${workspaceCache.refreshError}`);
    }
    if (userCache.refreshError) {
      partialErrors.push(`User metadata unavailable: ${userCache.refreshError}`);
    }
    if (!costSummaryResult && anthropicBucketWidth === '1d') {
      partialErrors.push('Cost report unavailable: displaying estimated Anthropic costs from token usage.');
    }

    onProgress?.('Processing Anthropic usage data...');

    const modelUsageMap = new Map<string, ModelUsageDetail>();
    const apiKeyMap = new Map<string, ApiKeyAccumulator>();
    const dailyUsageMap = new Map<string, DailyAnthropicUsageAggregate>();
    const usedWorkspaceIds = new Set<string>();
    const workspaceMetadata = workspaceCache?.data ?? {};

    let totalContextTokens = 0;
    let totalGeneratedTokens = 0;
    let totalCachedTokens = 0;
    let totalCost = 0;
    const modelTokenTotals = new Map<string, number>();

    usageData.forEach(entry => {
      const timestamp = entry.timestamp ? new Date(entry.timestamp) : new Date();
      const dayKey = formatUtcBucketKey(timestamp, anthropicBucketWidth);
      const breakdown = getAnthropicTokenBreakdown(entry);
      const costBreakdown = calculateAnthropicCostBreakdown(entry);
      const cachedTokens = breakdown.cacheRead + breakdown.cacheWrite5m + breakdown.cacheWrite1h + breakdown.cacheWriteOther;
      const totalTokens = breakdown.totalTokens;
      // Discount cache_read in request estimation: cache_read correlates with
      // request count but also scales with context size, inflating the count
      // for heavy cache users. We count it at a reduced weight.
      const effectiveTokensForRequests = (totalTokens - breakdown.cacheRead) + breakdown.cacheRead * CACHE_READ_REQUEST_WEIGHT;
      const isClaudeCodeEntry = metadataCache.data[entry.api_key_id ?? '']?.name?.startsWith('claude_code_') ?? false;
      const tokensPerRequest = isClaudeCodeEntry ? TOKENS_PER_REQUEST_CLAUDE_CODE : TOKENS_PER_REQUEST_API;
      const requestEstimate = effectiveTokensForRequests > 0 ? effectiveTokensForRequests / tokensPerRequest : 0;

      totalContextTokens += breakdown.totalInputTokens;
      totalGeneratedTokens += breakdown.outputTokens;
      totalCachedTokens += cachedTokens;
      totalCost += costBreakdown.totalCost;

      // Model aggregation
      const modelName = entry.model ?? 'unknown';
      if (!modelUsageMap.has(modelName)) {
        modelUsageMap.set(modelName, {
          model: modelName,
          contextTokens: 0,
          generatedTokens: 0,
          cachedTokens: 0,
          cost: 0,
          numberOfRequests: 0,
          provider: 'anthropic'
        });
      }
      const modelStat = modelUsageMap.get(modelName)!;
      modelStat.contextTokens += breakdown.totalInputTokens;
      modelStat.generatedTokens += breakdown.outputTokens;
      modelStat.cachedTokens += cachedTokens;
      modelStat.cost += costBreakdown.totalCost;
      modelStat.numberOfRequests += requestEstimate;
      modelTokenTotals.set(modelName, (modelTokenTotals.get(modelName) ?? 0) + totalTokens);

      // Track last used date
      const entryDateStr = dayKey.slice(0, 10); // Extract YYYY-MM-DD from bucket key
      if (!modelStat.lastUsed || entryDateStr > modelStat.lastUsed) {
        modelStat.lastUsed = entryDateStr;
      }

      // API key aggregation
      const apiKeyId = entry.api_key_id ?? CONSOLE_KEY_ID;
      if (!apiKeyMap.has(apiKeyId)) {
        const metadata = metadataCache.data[apiKeyId];
        const name = metadata?.name || metadata?.partial_key_hint || apiKeyId;
        const workspaceId = metadata?.workspace_id ?? entry.workspace_id ?? undefined;
        const workspaceName = workspaceId ? workspaceMetadata[workspaceId]?.name ?? workspaceId : undefined;
        if (workspaceId) {
          usedWorkspaceIds.add(workspaceId);
        }
        apiKeyMap.set(apiKeyId, {
          id: apiKeyId,
          name,
          workspaceId,
          workspaceName,
          requests: 0,
          entryCount: 0,
          contextTokens: 0,
          generatedTokens: 0,
          cachedTokens: 0,
          cost: 0,
          cachedCost: 0,
          modelUsage: new Map(),
          modelCosts: new Map(),
          firstTimestamp: timestamp.getTime(),
          lastTimestamp: timestamp.getTime(),
          dayKeys: new Set<string>(),
          provider: 'anthropic',
          totalTokens: 0,
          effectiveTokensForRequests: 0,
          modelTokenSums: new Map(),
          modelEffectiveTokenSums: new Map(),
          dailyModelCosts: new Map()
        });
      }

      const apiKeyStat = apiKeyMap.get(apiKeyId)!;
      apiKeyStat.entryCount += 1;
      apiKeyStat.totalTokens += totalTokens;
      apiKeyStat.effectiveTokensForRequests += effectiveTokensForRequests;
      apiKeyStat.contextTokens += breakdown.totalInputTokens;
      apiKeyStat.generatedTokens += breakdown.outputTokens;
      apiKeyStat.cachedTokens += cachedTokens;
      apiKeyStat.cost += costBreakdown.totalCost;
      const cachedCostPortion = costBreakdown.cacheReadCost +
        costBreakdown.cacheWrite5mCost +
        costBreakdown.cacheWrite1hCost +
        costBreakdown.cacheWriteOtherCost;
      apiKeyStat.cachedCost += cachedCostPortion;
      apiKeyStat.firstTimestamp = Math.min(apiKeyStat.firstTimestamp, timestamp.getTime());
      apiKeyStat.lastTimestamp = Math.max(apiKeyStat.lastTimestamp, timestamp.getTime());
      apiKeyStat.dayKeys.add(dayKey);

      if (apiKeyStat.workspaceId) {
        usedWorkspaceIds.add(apiKeyStat.workspaceId);
      }

      if (!apiKeyStat.modelUsage.has(modelName)) {
        apiKeyStat.modelUsage.set(modelName, {
          model: modelName,
          contextTokens: 0,
          generatedTokens: 0,
          cachedTokens: 0,
          cost: 0,
          numberOfRequests: 0,
          provider: 'anthropic'
        });
      }
      const perKeyModel = apiKeyStat.modelUsage.get(modelName)!;
      perKeyModel.contextTokens += breakdown.totalInputTokens;
      perKeyModel.generatedTokens += breakdown.outputTokens;
      perKeyModel.cachedTokens += cachedTokens;
      perKeyModel.cost += costBreakdown.totalCost;
      perKeyModel.numberOfRequests += requestEstimate;
      if (!perKeyModel.lastUsed || entryDateStr > perKeyModel.lastUsed) {
        perKeyModel.lastUsed = entryDateStr;
      }
      apiKeyStat.modelTokenSums.set(modelName, (apiKeyStat.modelTokenSums.get(modelName) ?? 0) + totalTokens);
      apiKeyStat.modelEffectiveTokenSums.set(modelName, (apiKeyStat.modelEffectiveTokenSums.get(modelName) ?? 0) + effectiveTokensForRequests);

      apiKeyStat.modelCosts.set(modelName, (apiKeyStat.modelCosts.get(modelName) ?? 0) + costBreakdown.totalCost);

      // Accumulate per-day per-model cost for direct daily cost charts
      const dailyDateKey = dayKey.slice(0, 10);
      if (!apiKeyStat.dailyModelCosts.has(dailyDateKey)) {
        apiKeyStat.dailyModelCosts.set(dailyDateKey, new Map());
      }
      apiKeyStat.dailyModelCosts.get(dailyDateKey)!.set(
        modelName,
        (apiKeyStat.dailyModelCosts.get(dailyDateKey)!.get(modelName) ?? 0) + costBreakdown.totalCost
      );

      // Daily usage aggregation
      if (!dailyUsageMap.has(dayKey)) {
        dailyUsageMap.set(dayKey, createAnthropicDailyAggregate(dayKey));
      }
      const dailyStat = dailyUsageMap.get(dayKey)!;
      dailyStat.totalTokens += totalTokens;
      dailyStat.cost += costBreakdown.totalCost;
      dailyStat.contextTokens += breakdown.totalInputTokens;
      dailyStat.generatedTokens += breakdown.outputTokens;
      dailyStat.cachedTokens += cachedTokens;
      if (!dailyStat.breakdowns) {
        dailyStat.breakdowns = {};
      }
      if (!dailyStat.breakdowns.anthropic) {
        dailyStat.breakdowns.anthropic = {
          provider: 'anthropic',
          requests: 0,
          cost: 0,
          contextTokens: 0,
          generatedTokens: 0,
          cachedTokens: 0,
          isEstimatedRequests: true
        };
      }
      const anthropicBreakdown = dailyStat.breakdowns.anthropic!;
      anthropicBreakdown.requests += requestEstimate;
      anthropicBreakdown.cost += costBreakdown.totalCost;
      anthropicBreakdown.contextTokens += breakdown.totalInputTokens;
      anthropicBreakdown.generatedTokens += breakdown.outputTokens;
      anthropicBreakdown.cachedTokens += cachedTokens;
      dailyStat.models[modelName] = (dailyStat.models[modelName] ?? 0) + totalTokens;
      if (!dailyStat.modelCosts) dailyStat.modelCosts = {};
      dailyStat.modelCosts[modelName] = (dailyStat.modelCosts[modelName] ?? 0) + costBreakdown.totalCost;
      dailyStat.apiKeys.add(apiKeyId);
      dailyStat.uniqueApiKeys = dailyStat.apiKeys.size;
      dailyStat.modelTokenSums[modelName] = (dailyStat.modelTokenSums[modelName] ?? 0) + totalTokens;
      dailyStat.modelRequestEstimates[modelName] = (dailyStat.modelRequestEstimates[modelName] ?? 0) + requestEstimate;
    });

    if (costSummaryResult && costSummaryResult.dailyCosts.size > 0 && anthropicBucketWidth === '1d') {
      const estimatedTotalCost = totalCost;
      let overriddenDays = 0;

      costSummaryResult.dailyCosts.forEach((reportedCost, dayKey) => {
        if (!Number.isFinite(reportedCost)) {
          return;
        }
        let dailyStat = dailyUsageMap.get(dayKey);
        if (!dailyStat) {
          dailyStat = createAnthropicDailyAggregate(dayKey);
          dailyUsageMap.set(dayKey, dailyStat);
        }

        const previousCost = Number.isFinite(dailyStat.cost) ? dailyStat.cost : 0;
        dailyStat.cost = reportedCost;
        if (!dailyStat.breakdowns) {
          dailyStat.breakdowns = {};
        }
        if (!dailyStat.breakdowns.anthropic) {
          dailyStat.breakdowns.anthropic = {
            provider: 'anthropic',
            requests: dailyStat.requests ?? 0,
            cost: 0,
            contextTokens: dailyStat.contextTokens ?? 0,
            generatedTokens: dailyStat.generatedTokens ?? 0,
            cachedTokens: dailyStat.cachedTokens ?? 0,
            isEstimatedRequests: true
          };
        }
        dailyStat.breakdowns.anthropic.cost = reportedCost;

        totalCost += reportedCost - previousCost;
        overriddenDays += 1;
      });

      if (!Number.isFinite(totalCost) || totalCost < 0) {
        totalCost = 0;
      }

      if (estimatedTotalCost > 0 && totalCost > 0) {
        const scale = totalCost / estimatedTotalCost;
        if (Number.isFinite(scale) && scale >= 0 && Math.abs(scale - 1) > 1e-9) {
          scaleCostBreakdowns(scale, apiKeyMap, modelUsageMap);
        }
      } else if (estimatedTotalCost <= 0 && totalCost > 0) {
        distributeCostByTokenShare(totalCost, apiKeyMap, modelUsageMap, modelTokenTotals);
      }

      const totalDaysWithUsage = dailyUsageMap.size;
      if (overriddenDays > 0 && overriddenDays < totalDaysWithUsage) {
        partialErrors.push(`Cost reconciliation partial: ${overriddenDays}/${totalDaysWithUsage} days matched cost report data. Remaining days use token-based estimates.`);
      }

      debugLog('[Anthropic Usage] Applied cost reconciliation', {
        reportedTotalCost: costSummaryResult.totalCost,
        estimatedTotalCost,
        reconciledTotalCost: totalCost,
        overriddenDays,
        costRows: costSummaryResult.rows
      });
    } else if (costSummaryResult && costSummaryResult.dailyCosts.size > 0 && anthropicBucketWidth !== '1d') {
      debugLog('[Anthropic Usage] Skipped daily cost reconciliation for non-daily buckets', {
        bucketWidth: anthropicBucketWidth
      });
    }

    // Build user lookup: created_by.id -> user email/name
    const userLookup = new Map<string, { email: string; name: string }>();
    if (userCache?.data) {
      Object.values(userCache.data).forEach(user => {
        if (user.id) {
          userLookup.set(user.id, { email: user.email, name: user.name });
        }
      });
    }

    // Inject unused API keys from metadataCache into apiKeyMap
    if (metadataCache?.data) {
      Object.values(metadataCache.data).forEach(keyMeta => {
        if (apiKeyMap.has(keyMeta.id)) return; // Already has usage data
        const workspaceId = keyMeta.workspace_id ?? undefined;
        const workspaceName = workspaceId ? workspaceMetadata[workspaceId]?.name ?? workspaceId : undefined;
        if (workspaceId) usedWorkspaceIds.add(workspaceId);
        apiKeyMap.set(keyMeta.id, {
          id: keyMeta.id,
          name: keyMeta.name || keyMeta.partial_key_hint || keyMeta.id,
          workspaceId,
          workspaceName,
          requests: 0, entryCount: 0,
          contextTokens: 0, generatedTokens: 0, cachedTokens: 0,
          cost: 0, cachedCost: 0,
          modelUsage: new Map(), modelCosts: new Map(),
          firstTimestamp: keyMeta.created_at ? Date.parse(keyMeta.created_at) : Date.now(),
          lastTimestamp: keyMeta.created_at ? Date.parse(keyMeta.created_at) : Date.now(),
          dayKeys: new Set(), provider: 'anthropic',
          totalTokens: 0, effectiveTokensForRequests: 0,
          modelTokenSums: new Map(), modelEffectiveTokenSums: new Map(),
          dailyModelCosts: new Map()
        });
      });
    }

    const apiKeyUsage: ApiKeyUsageDetail[] = Array.from(apiKeyMap.values()).map(stat => {
      // Use API key created_at from metadata as firstSeen fallback
      // (Anthropic Usage API only returns bucket timestamps, not key activity dates)
      const metadata = metadataCache.data[stat.id];
      const createdAtMs = metadata?.created_at ? Date.parse(metadata.created_at) : NaN;
      const effectiveFirstTimestamp = Number.isFinite(createdAtMs)
        ? Math.min(createdAtMs, stat.firstTimestamp)
        : stat.firstTimestamp;
      const firstSeen = new Date(effectiveFirstTimestamp);
      const lastSeen = new Date(stat.lastTimestamp);
      const diffDays = Math.max(1, Math.ceil((stat.lastTimestamp - effectiveFirstTimestamp) / MS_PER_DAY));
      // Resolve owner user identity from created_by.id
      const createdByUserId = metadata?.created_by?.id;
      const ownerUser = createdByUserId ? userLookup.get(createdByUserId) : undefined;
      // Only set ownerEmail for Claude Code keys (self-provisioned by each user).
      // Regular API keys are created by admins, so created_by.id is the admin's ID.
      const isClaudeCodeKey = stat.name.startsWith('claude_code_');
      const ownerEmail = isClaudeCodeKey && ownerUser?.email ? ownerUser.email : undefined;
      // For regular API keys, created_by identifies the administrator who created
      // the key, not necessarily the person consuming it. Only Claude Code keys
      // are self-provisioned strongly enough to treat created_by as ownership.
      const ownerUserId = isClaudeCodeKey ? createdByUserId : undefined;
      const keyTokensPerRequest = isClaudeCodeKey ? TOKENS_PER_REQUEST_CLAUDE_CODE : TOKENS_PER_REQUEST_API;
      const estimatedRequests = stat.effectiveTokensForRequests > 0
        ? Math.max(1, Math.round(stat.effectiveTokensForRequests / keyTokensPerRequest))
        : stat.entryCount > 0 ? Math.max(1, stat.entryCount) : 0;
      stat.requests = estimatedRequests;

      const cachedPercentage = stat.contextTokens > 0
        ? (stat.cachedTokens / stat.contextTokens) * 100
        : 0;
      const costPer100Requests = estimatedRequests > 0
        ? (stat.cost / estimatedRequests) * 100
        : 0;

      const modelUsageEntries = Object.fromEntries(
        Array.from(stat.modelUsage.entries()).map(([key, value]) => {
          const effectiveSum = stat.modelEffectiveTokenSums.get(key) ?? 0;
          const modelRequests = effectiveSum > 0
            ? Math.max(1, Math.round(effectiveSum / keyTokensPerRequest))
            : Math.max(1, Math.round(value.numberOfRequests));
          return [key, { ...value, numberOfRequests: modelRequests }];
        })
      );

      return {
        id: stat.id,
        name: stat.name,
        rank: determineRank(estimatedRequests, diffDays),
        requests: estimatedRequests,
        numberOfRequests: estimatedRequests,
        days: diffDays,
        firstSeen: firstSeen.toISOString().split('T')[0],
        lastSeen: lastSeen.toISOString().split('T')[0],
        contextTokens: stat.contextTokens,
        generatedTokens: stat.generatedTokens,
        cachedTokens: stat.cachedTokens,
        cost: stat.cost,
        cachedCost: stat.cachedCost,
        cachedPercentage,
        costPer100Requests,
        status: stat.entryCount > 0 ? 'Active' : 'Inactive',
        modelCosts: Object.fromEntries(stat.modelCosts),
        dailyModelCosts: Object.fromEntries(
          Array.from(stat.dailyModelCosts.entries()).map(([date, modelMap]) => [
            date, Object.fromEntries(modelMap)
          ])
        ),
        modelUsage: modelUsageEntries,
        projectId: stat.workspaceId,
        projectIds: stat.workspaceId ? [stat.workspaceId] : undefined,
        projectName: stat.workspaceName ?? stat.workspaceId,
        provider: 'anthropic',
        totalCost: stat.cost,
        sourceIds: [stat.id],
        ownerUserId,
        ownerEmail,
        ownerType: isClaudeCodeKey ? 'user' : undefined,
        ownerIdentityTrusted: isClaudeCodeKey,
        costQuality: 'estimated'
      };
    });

    const dailyUsage: DailyUsageData[] = Array.from(dailyUsageMap.values())
      .map(day => {
        const accumulatedRequests = day.breakdowns?.anthropic?.requests ?? 0;
        const estimatedRequests = accumulatedRequests > 0
          ? Math.max(1, Math.round(accumulatedRequests))
          : Math.max(1, day.apiKeys.size);
        const modelEntries = Object.fromEntries(
          Object.entries(day.modelRequestEstimates).map(([modelName, reqEstimate]) => [
            modelName,
            Math.max(1, Math.round(reqEstimate))
          ])
        );
        const breakdowns: Partial<Record<UsageProvider, ProviderUsageMetrics>> = {};
        const anthropicBreakdown: ProviderUsageMetrics = {
          provider: 'anthropic' as UsageProvider,
          requests: estimatedRequests,
          cost: day.cost,
          contextTokens: day.contextTokens,
          generatedTokens: day.generatedTokens,
          cachedTokens: day.cachedTokens,
          isEstimatedRequests: true
        };
        breakdowns.anthropic = anthropicBreakdown;
        const result: DailyUsageData = {
          date: day.date,
          requests: estimatedRequests,
          cost: day.cost,
          contextTokens: day.contextTokens,
          generatedTokens: day.generatedTokens,
          cachedTokens: day.cachedTokens,
          models: modelEntries,
          uniqueApiKeys: day.apiKeys.size,
          apiKeys: day.apiKeys,
          num_requests: estimatedRequests,
          provider: 'anthropic' as UsageProvider,
          breakdowns,
          isEstimatedRequests: true
        };
        return result;
      })
      .sort((a, b) => (a.date < b.date ? -1 : 1));

    const projects = Array.from(usedWorkspaceIds).map(id => ({
      id,
      name: workspaceMetadata[id]?.name ?? id,
      status: 'active' as const,
      provider: 'anthropic' as const
    }));

    const totalRequests = apiKeyUsage.reduce((sum, key) => sum + key.numberOfRequests, 0);
    const modelUsage = Array.from(modelUsageMap.entries()).map(([_modelName, detail]) => {
      const estimatedModelRequests = detail.numberOfRequests > 0
        ? Math.max(1, Math.round(detail.numberOfRequests))
        : 1;
      return { ...detail, numberOfRequests: estimatedModelRequests };
    });

    const usageDetails: UsageDetailsData = {
      modelUsage,
      apiKeyUsage,
      totalRequests,
      totalCost,
      totalContextTokens,
      totalGeneratedTokens,
      totalCachedTokens,
      totalApiKeys: metadataCache?.data ? Object.keys(metadataCache.data).length : apiKeyUsage.length,
      displayedApiKeys: apiKeyUsage.length,
      dailyUsage,
      projects,
      dataWindow: {
        start: startDate.toISOString(),
        end: endDate.toISOString(),
        timezoneOffsetMinutes: new Date().getTimezoneOffset(),
        coverageHours: Math.max(1, Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60))),
        expectedHours: Math.max(1, Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60)))
      },
      cacheTimestamp: Date.now(),
      partialErrors: partialErrors.length > 0 ? partialErrors : undefined,
    };

    return {
      success: true,
      data: usageDetails
    };
  } catch (error: unknown) {
    console.error('Failed to fetch Anthropic usage details:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch Anthropic usage data'
    };
  }
};
